import { useState, useCallback, useMemo, useEffect } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type {
  Patient,
  Coverage,
  DocumentReference,
  Binary,
  Bundle,
  Resource,
} from '@medplum/fhirtypes';
import { OperationOutcomeError } from '@medplum/core';
import type { LogisticsData, GovernmentIdType, DocumentType } from '../types/logistics';
import { DOCUMENT_TYPE_CODES } from '../types/logistics';

const PHARMACY_EXTENSION_URL = 'http://precisionlongevity.io/fhir/StructureDefinition/preferred-pharmacy';

export function extractLogisticsData(
  coverage: Coverage | undefined,
  documents: DocumentReference[],
  patient: Patient | undefined
): LogisticsData {
  // Extract insurance data from Coverage
  const insuranceProvider = coverage?.payor?.[0]?.display || '';
  const memberId = coverage?.identifier?.[0]?.value || coverage?.subscriberId || '';
  const groupClass = coverage?.class?.find(
    (c: { type?: { coding?: { code?: string }[] }; value?: string }) =>
      c.type?.coding?.[0]?.code === 'group'
  );
  const groupNumber = groupClass?.value || '';

  // Extract document URLs
  const getDocUrl = (typeCode: string) => {
    const doc = documents.find(
      d => d.type?.coding?.[0]?.code === typeCode
    );
    return doc?.content?.[0]?.attachment?.url || null;
  };

  const governmentIdUrl = getDocUrl(DOCUMENT_TYPE_CODES['government-id'].code);
  const insuranceCardFrontUrl = getDocUrl(DOCUMENT_TYPE_CODES['insurance-card-front'].code);
  const insuranceCardBackUrl = getDocUrl(DOCUMENT_TYPE_CODES['insurance-card-back'].code);

  // Extract pharmacy from patient extension
  const pharmacyExt = patient?.extension?.find(
    (e: { url?: string; valueString?: string }) => e.url === PHARMACY_EXTENSION_URL
  );
  const pharmacy = pharmacyExt?.valueString || null;

  // Determine government ID type from document (default to drivers-license)
  const govIdDoc = documents.find(
    d => d.type?.coding?.[0]?.code === DOCUMENT_TYPE_CODES['government-id'].code
  );
  const governmentIdType: GovernmentIdType =
    (govIdDoc?.type?.coding?.[0]?.display?.toLowerCase().includes('passport')
      ? 'passport'
      : govIdDoc?.type?.coding?.[0]?.display?.toLowerCase().includes('state')
        ? 'state-id'
        : 'drivers-license') as GovernmentIdType;

  return {
    insuranceProvider,
    memberId,
    groupNumber,
    governmentIdUrl,
    governmentIdType,
    insuranceCardFrontUrl,
    insuranceCardBackUrl,
    pharmacy,
  };
}

export function useLogistics() {
  const medplum = useMedplum();
  const baseProfile = useMedplumProfile() as Resource | undefined;

  const [coverage, setCoverage] = useState<Coverage | undefined>(undefined);
  const [documents, setDocuments] = useState<DocumentReference[]>([]);
  const [patient, setPatient] = useState<Patient | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patientId = baseProfile?.id;
  const resourceType = (baseProfile as { resourceType?: string })?.resourceType;

  // Load existing data from Medplum
  const loadData = useCallback(async () => {
    if (!patientId || !resourceType) return;

    try {
      setIsLoading(true);
      setError(null);

      // Fetch patient/profile first (required)
      const patientResult = await medplum.readResource(resourceType as 'Patient', patientId);
      setPatient(patientResult as Patient);

      // Fetch optional resources - don't fail if not found
      try {
        const coverageResult = await medplum.search('Coverage', {
          beneficiary: `Patient/${patientId}`,
          status: 'active',
          _count: '1',
        });
        const coverageBundle = coverageResult as Bundle<Coverage>;
        setCoverage(coverageBundle.entry?.[0]?.resource);
      } catch {
        // No coverage found - that's okay
        setCoverage(undefined);
      }

      try {
        const documentsResult = await medplum.search('DocumentReference', {
          subject: `Patient/${patientId}`,
          status: 'current',
        });
        const docBundle = documentsResult as Bundle<DocumentReference>;
        setDocuments(
          docBundle.entry?.map(
            (e: { resource?: DocumentReference }) => e.resource as DocumentReference
          ) || []
        );
      } catch {
        // No documents found - that's okay
        setDocuments([]);
      }
    } catch (err) {
      console.error('Failed to load patient data:', err);
      if (err instanceof OperationOutcomeError) {
        console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
      }
      setError('Unable to load patient data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [medplum, patientId, resourceType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const logisticsData = useMemo(
    () => extractLogisticsData(coverage, documents, patient),
    [coverage, documents, patient]
  );

  const isComplete = useMemo(() => {
    return !!(
      logisticsData.insuranceProvider &&
      logisticsData.memberId &&
      logisticsData.governmentIdUrl &&
      logisticsData.insuranceCardFrontUrl &&
      logisticsData.insuranceCardBackUrl
    );
  }, [logisticsData]);

  const hasAnyData = useMemo(() => {
    return !!(
      logisticsData.insuranceProvider ||
      logisticsData.memberId ||
      logisticsData.groupNumber ||
      logisticsData.governmentIdUrl ||
      logisticsData.insuranceCardFrontUrl ||
      logisticsData.insuranceCardBackUrl ||
      logisticsData.pharmacy
    );
  }, [logisticsData]);

  // Delete a document from Medplum
  const deleteDocument = useCallback(
    async (documentType: DocumentType): Promise<boolean> => {
      if (!patientId) return false;

      try {
        const typeCode = DOCUMENT_TYPE_CODES[documentType];
        const existingDoc = documents.find(
          d => d.type?.coding?.[0]?.code === typeCode.code
        );

        if (existingDoc?.id) {
          await medplum.deleteResource('DocumentReference', existingDoc.id);
          await loadData();
          return true;
        }
        return false;
      } catch (err) {
        console.error('Failed to delete document:', err);
        if (err instanceof OperationOutcomeError) {
          console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
        }
        setError('Unable to delete document. Please try again.');
        return false;
      }
    },
    [medplum, patientId, documents, loadData]
  );

  // Upload a document (image) to Medplum
  const uploadDocument = useCallback(
    async (
      file: File,
      documentType: DocumentType,
      idType?: GovernmentIdType
    ): Promise<string | null> => {
      if (!patientId) return null;

      try {
        // Create Binary resource with file data
        const binary = await medplum.createBinary({
          data: file,
          filename: file.name,
          contentType: file.type,
        });

        // Get or create document type coding
        const typeCode = DOCUMENT_TYPE_CODES[documentType];
        const displayText =
          documentType === 'government-id' && idType
            ? idType === 'passport'
              ? 'Passport'
              : idType === 'state-id'
                ? 'State ID'
                : 'Driver License'
            : typeCode.display;

        // Check if document already exists for this type
        const existingDoc = documents.find(
          d => d.type?.coding?.[0]?.code === typeCode.code
        );

        if (existingDoc?.id) {
          // Update existing document
          const updated: DocumentReference = {
            ...existingDoc,
            date: new Date().toISOString(),
            type: {
              coding: [{ ...typeCode, display: displayText }],
            },
            content: [
              {
                attachment: {
                  url: `Binary/${(binary as Binary).id}`,
                  contentType: file.type,
                  title: file.name,
                },
              },
            ],
          };
          await medplum.updateResource(updated);
        } else {
          // Create new DocumentReference
          const docRef: DocumentReference = {
            resourceType: 'DocumentReference',
            status: 'current',
            type: {
              coding: [{ ...typeCode, display: displayText }],
            },
            subject: { reference: `Patient/${patientId}` },
            date: new Date().toISOString(),
            content: [
              {
                attachment: {
                  url: `Binary/${(binary as Binary).id}`,
                  contentType: file.type,
                  title: file.name,
                },
              },
            ],
          };
          await medplum.createResource(docRef);
        }

        // Reload documents
        await loadData();

        // Return the binary URL for preview
        return medplum.fhirUrl('Binary', (binary as Binary).id!).toString();
      } catch (err) {
        console.error('Failed to upload document:', err);
        if (err instanceof OperationOutcomeError) {
          console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
        }
        setError('Unable to upload document. Please try again.');
        return null;
      }
    },
    [medplum, patientId, documents, loadData]
  );

  // Save insurance data (Coverage resource)
  const saveInsurance = useCallback(
    async (
      data: {
        insuranceProvider: string;
        memberId: string;
        groupNumber: string;
      },
      skipLoadingState = false
    ): Promise<boolean> => {
      if (!patientId) {
        setError('Patient data not loaded');
        return false;
      }

      try {
        if (!skipLoadingState) {
          setIsSaving(true);
          setError(null);
        }

        const coverageData: Coverage = {
          resourceType: 'Coverage',
          id: coverage?.id,
          status: 'active',
          beneficiary: { reference: `Patient/${patientId}` },
          subscriber: { reference: `Patient/${patientId}` },
          relationship: {
            coding: [
              {
                system: 'http://terminology.hl7.org/CodeSystem/subscriber-relationship',
                code: 'self',
              },
            ],
          },
          payor: [{ display: data.insuranceProvider }],
          identifier: data.memberId
            ? [
                {
                  type: {
                    coding: [
                      {
                        system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
                        code: 'MB',
                        display: 'Member Number',
                      },
                    ],
                  },
                  value: data.memberId,
                },
              ]
            : undefined,
          subscriberId: data.memberId || undefined,
          class: data.groupNumber
            ? [
                {
                  type: {
                    coding: [
                      {
                        system: 'http://terminology.hl7.org/CodeSystem/coverage-class',
                        code: 'group',
                      },
                    ],
                  },
                  value: data.groupNumber,
                },
              ]
            : undefined,
        };

        if (coverage?.id) {
          await medplum.updateResource(coverageData);
        } else {
          await medplum.createResource(coverageData);
        }

        await loadData();
        return true;
      } catch (err) {
        console.error('Failed to save insurance:', err);
        if (err instanceof OperationOutcomeError) {
          console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
        }
        setError('Unable to save insurance data. Please try again.');
        return false;
      } finally {
        if (!skipLoadingState) {
          setIsSaving(false);
        }
      }
    },
    [medplum, patientId, coverage?.id, loadData]
  );

  // Save pharmacy to patient extension
  const savePharmacy = useCallback(
    async (pharmacyText: string, skipLoadingState = false): Promise<boolean> => {
      if (!patientId || !resourceType) {
        setError('Patient data not loaded');
        return false;
      }

      try {
        if (!skipLoadingState) {
          setIsSaving(true);
          setError(null);
        }

        // Fetch current patient to avoid stale closure issues
        const currentPatient = await medplum.readResource(resourceType as 'Patient', patientId) as Patient;

        // Update patient with pharmacy extension
        const existingExtensions = currentPatient.extension?.filter(
          (e: { url?: string }) => e.url !== PHARMACY_EXTENSION_URL
        ) || [];

        const updatedPatient: Patient = {
          ...currentPatient,
          extension: [
            ...existingExtensions,
            {
              url: PHARMACY_EXTENSION_URL,
              valueString: pharmacyText,
            },
          ],
        };

        await medplum.updateResource(updatedPatient);
        await loadData();
        return true;
      } catch (err) {
        console.error('Failed to save pharmacy:', err);
        if (err instanceof OperationOutcomeError) {
          console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
        }
        setError('Unable to save pharmacy. Please try again.');
        return false;
      } finally {
        if (!skipLoadingState) {
          setIsSaving(false);
        }
      }
    },
    [medplum, patientId, resourceType, loadData]
  );

  // Save all logistics data at once
  const saveAll = useCallback(
    async (data: {
      insuranceProvider: string;
      memberId: string;
      groupNumber: string;
      pharmacy: string;
    }): Promise<boolean> => {
      if (!patientId || !patient) {
        setError('Patient data not loaded. Please refresh and try again.');
        return false;
      }

      // Validate required fields
      if (data.insuranceProvider && !data.memberId) {
        setError('Please provide a Member ID with your insurance information.');
        return false;
      }

      try {
        setIsSaving(true);
        setError(null);

        // Pass skipLoadingState=true to avoid state conflicts
        const insuranceSuccess = await saveInsurance(
          {
            insuranceProvider: data.insuranceProvider,
            memberId: data.memberId,
            groupNumber: data.groupNumber,
          },
          true
        );

        if (!insuranceSuccess) {
          return false;
        }

        if (data.pharmacy) {
          const pharmacySuccess = await savePharmacy(data.pharmacy, true);
          if (!pharmacySuccess) {
            return false;
          }
        }

        return true;
      } catch (err) {
        console.error('Failed to save logistics:', err);
        if (err instanceof OperationOutcomeError) {
          console.error('FHIR OperationOutcome:', JSON.stringify(err.outcome, null, 2));
        }
        setError('Unable to save. Please try again.');
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [patientId, patient, saveInsurance, savePharmacy]
  );

  return {
    logisticsData,
    isLoading,
    isSaving,
    error,
    isComplete,
    hasAnyData,
    uploadDocument,
    deleteDocument,
    saveInsurance,
    savePharmacy,
    saveAll,
    refresh: loadData,
  };
}
